import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { fmtErr } from "@/lib/api";
import { Lock, ShieldCheck } from "lucide-react";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const nav = useNavigate();
    const [pw, setPw] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!token) return setErr("This reset link is missing its token. Please use the link from your email.");
        if (pw.length < 8) return setErr("Password must be at least 8 characters");
        if (pw !== confirm) return setErr("Passwords do not match");
        setBusy(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: pw });
            setDone(true);
        } catch (e2) { setErr(fmtErr(e2.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-brand-ink p-4" data-testid="reset-password-page">
            <div className="bg-white rounded-md shadow-2xl w-[440px] max-w-full p-8">
                <div className="flex items-center gap-2 mb-5">
                    <img src="/qube-logo.svg" alt="Qube" className="w-8 h-8 rounded-sm" />
                    <div className="font-display font-bold">Qube <span className="text-muted-foreground font-medium">Hub</span></div>
                </div>
                {done ? (
                    <div className="text-center" data-testid="reset-done">
                        <ShieldCheck className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
                        <h1 className="font-display font-bold text-xl mb-1">Password reset</h1>
                        <p className="text-sm text-muted-foreground mb-5">Your password has been updated. You can now sign in with your new password.</p>
                        <button data-testid="reset-goto-login" onClick={() => nav("/login")} className="w-full bg-brand hover:bg-brand-hover text-white font-display font-semibold text-sm py-2.5 rounded-sm">Go to sign in</button>
                    </div>
                ) : (
                    <>
                        <h1 className="font-display font-bold text-xl mb-1">Choose a new password</h1>
                        <p className="text-sm text-muted-foreground mb-5">Enter and confirm your new password below.</p>
                        <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
                            {[["New password", pw, setPw, "reset-new"], ["Confirm password", confirm, setConfirm, "reset-confirm"]].map(([label, val, set, tid]) => (
                                <div key={tid}>
                                    <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-display block mb-1.5">{label}</label>
                                    <div className="flex items-center border border-input rounded-sm bg-white focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20">
                                        <Lock className="w-4 h-4 text-muted-foreground mx-3" />
                                        <input type="password" value={val} onChange={(e) => set(e.target.value)} data-testid={tid} required className="flex-1 py-2.5 pr-3 bg-transparent outline-none text-sm" />
                                    </div>
                                </div>
                            ))}
                            {err && <div className="text-xs text-destructive bg-destructive/10 border border-destructive/20 px-3 py-2 rounded-sm" data-testid="reset-error">{err}</div>}
                            <button type="submit" disabled={busy} data-testid="reset-submit" className="w-full bg-brand hover:bg-brand-hover text-white font-display font-semibold text-sm py-2.5 rounded-sm disabled:opacity-50">{busy ? "Saving…" : "Reset password"}</button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
